/**
 * solversApi — 「이 문제를 받은 학생」을 서버에 묻고, 표가 그릴 값으로 판정한다.
 *
 * 계약: `docs/problem-hub-v2/BE-20260911-reply-solvers-by-problem.md`(dev·운영 반영 `5e1bd02`).
 * 설계: `docs/problem-hub-v2/0911_SPEC_problem-student-tab.html` §4~§6.
 *
 * 한 번에 100명(서버 상한 5~100)씩 이어 받고 10장(1,000명)에서 멈춘다 — 시험지 탭(paperUsageApi)과 같은 상수다.
 * 정렬은 서버가 한다(마지막 출제 내림차순, problemApi.fetchProblemSolvers 주석) — 받은 순서가 곧 표의 순서다.
 *
 * 머리 줄의 수는 행을 세지 않고 서버 집계(summary)를 쓴다. 집계는 페이지와 무관한 범위 전체라
 * (회신 §2-1), 1,000명에서 목록이 잘려도 머리 줄의 수는 맞다.
 */
import { fetchProblemSolvers } from './problemApi';

/** 서버 상한(`api/v2/problems.py` — per_page 5~100) */
const PER_PAGE = 100;
/** 안전장치 — 이 장 수를 넘으면 여기까지만 받고 「덜 받았다」고 말한다. 말없이 오래 도는 것보다 낫다. */
const MAX_PAGES = 10;

/**
 * 최근 결과 네 갈래의 글자와 색.
 *
 * 정답·오답 색은 **시험지 채점 계열**이다(초록 600 · 빨강 600 — `ExamPapersV2/AssignPanel.jsx` 의 정답·오답 수,
 * `AssignmentPanel.jsx` 채점 버튼, `examPaperApi.accuracyTone`). 학습 「전체 테스트」 표는 브랜드 초록을 써서
 * 형제 둘이 갈려 있었고, 이 탭의 틀이 시험지 탭이라 그쪽을 정본으로 골랐다(오너 결정 2026-09-11).
 * 굵기를 함께 올린다 — 색만으로 내리면 색을 못 보는 사람에게 「정답」과 「무응답」이 같은 줄이 된다.
 */
export const SOLVER_RESULT = {
  ungraded: { label: '채점 전', cls: 'text-gray-400' },
  correct: { label: '정답', cls: 'font-semibold text-green-600' },
  wrong: { label: '오답', cls: 'font-semibold text-red-600' },
  unanswered: { label: '무응답', cls: 'text-gray-500' },
};

/**
 * 한 학생 줄의 최근 결과 — 'ungraded' | 'correct' | 'wrong' | 'unanswered'.
 *
 * **1차 기준은 `status` 다.** `latest_result` 의 null 은 「채점 전」(채점 기록이 없음)과 「무응답」(채점했는데
 * 답이 비었음) 두 뜻을 함께 가져서, 그것만 읽으면 둘이 한 칸에 섞인다. status 가 없는 응답이면 같은 사실인
 * `is_graded` 로 받친다 — 학생당 한 줄 모드는 is_graded, 기록당 한 줄 모드는 graded 로 이름이 갈려 있다(회신 §2).
 */
export function solverResultOf(item) {
  if (item == null) return null;
  const graded = item.status != null ? item.status === 'graded' : item.is_graded === true;
  if (!graded) return 'ungraded';
  if (item.latest_result === true) return 'correct';
  if (item.latest_result === false) return 'wrong';
  return 'unanswered';
}

/**
 * 한 장. 404 는 사람의 말로 바꿔 던진다 — 서버 문구는 영어(「Problem not found」)라 그대로 띠에 올리면
 * 무엇이 없는지 읽히지 않는다. 경로는 dev·운영 모두에 있으므로 404 는 문제가 없어졌다는 뜻이다(설계 §6).
 * 다른 실패(400·연결)는 공용 함수가 만든 문구 그대로 올린다.
 */
async function fetchPage(pid, page, getAccessToken) {
  try {
    const d = await fetchProblemSolvers(pid, {
      page, perPage: PER_PAGE, orderBy: 'last_assigned', order: 'desc',
    }, getAccessToken);
    return d || {};
  } catch (e) {
    if (e?.httpStatus === 404) {
      const err = new Error('이 문제를 찾을 수 없습니다(삭제됐을 수 있습니다).');
      err.httpStatus = 404;
      throw err;
    }
    throw e;
  }
}

const itemsOf = (d) => (Array.isArray(d?.items) ? d.items : []);

/**
 * 한 문제를 받은 학생 전부(최대 1,000명).
 * 반환 `{ items, summary, scope, totalItems, truncated }` — items 는 서버 항목 그대로다(표가 필드를 직접 읽는다).
 * summary·scope 는 첫 장의 것을 쓴다 — 장마다 같은 값이다. 옛 응답처럼 없으면 null 로 두고,
 * 화면은 그 조각을 그리지 않는다(지어내지 않는다).
 */
export async function fetchSolversForProblem(problemId, getAccessToken) {
  const pid = Number(problemId);
  // 라우트 파라미터는 문자열로 온다. 정수가 아닌 값은 서버가 404 로 돌려주므로 왕복 전에 같은 말을 한다.
  if (!Number.isInteger(pid) || pid < 0) throw new Error('문제 번호가 정수가 아닙니다.');

  const first = await fetchPage(pid, 1, getAccessToken);
  let items = [...itemsOf(first)];
  const totalPages = Number.isInteger(first.total_pages) ? first.total_pages : 1;
  const lastPage = Math.min(totalPages, MAX_PAGES);
  for (let p = 2; p <= lastPage; p += 1) {
    // eslint-disable-next-line no-await-in-loop -- 장은 순서대로 이어 붙인다(정렬을 서버가 하므로 순서가 곧 결과다)
    const d = await fetchPage(pid, p, getAccessToken);
    items = items.concat(itemsOf(d));
  }

  return {
    items,
    summary: first.summary ?? null,
    scope: first.scope ?? null,
    totalItems: Number.isInteger(first.total_items) ? first.total_items : items.length,
    truncated: totalPages > MAX_PAGES,
  };
}
